import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import portraitEva from '../assets/portrait-eva.webp'
import portraitChristina from '../assets/portrait-christina.webp'
import portraitAgustin from '../assets/portrait-agustin.webp'

const memberData = [
  {
    key: 'eva',
    name: 'Eva-Maria Köberle',
    photo: portraitEva,
    hashtag: '#gooddesignisgoodbusiness',
  },
  {
    key: 'christina',
    name: 'Christina Mathis',
    photo: portraitChristina,
    hashtag: '#whatsyourstory',
  },
  {
    key: 'agustin',
    name: 'Agustín Ramos Peruzzo',
    photo: portraitAgustin,
    hashtag: '#turningideasintoreality',
  },
]

export default function Team() {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})

  const toggle = (name: string) => setExpanded((prev) => ({ ...prev, [name]: !prev[name] }))

  return (
    <section id="team" className="bg-espresso">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6 }}
        className="text-center py-20 px-6"
      >
        <p className="font-body text-sand/50 text-xs tracking-widest uppercase mb-3">
          {t('team.kicker')}
        </p>
        <h2 className="font-headline text-sand text-4xl md:text-5xl mb-6">{t('team.h2')}</h2>
        <p className="font-body text-sand/85 text-base leading-relaxed max-w-2xl mx-auto">
          {t('team.intro')}
        </p>
      </motion.div>

      {/* Team Grid */}
      <div className="max-w-7xl mx-auto px-6 pb-24 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-12">
        {memberData.map((member, index) => (
          <motion.div
            key={member.name}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: index * 0.15 }}
            className="relative flex flex-col items-center"
          >
            {/* Photo */}
            <div className="relative z-10 w-56 h-56 sm:w-72 sm:h-72 shadow-2xl">
              <img
                src={member.photo}
                alt={member.name}
                loading="lazy"
                width={600}
                height={600}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Info */}
            <p className="font-body text-sand/50 text-xs uppercase tracking-widest mt-6 mb-1">
              {t(`team.${member.key}.role`)}
            </p>
            <p className="font-body text-sand/70 text-sm leading-relaxed text-center max-w-xs">
              {t(`team.${member.key}.bio`)}
            </p>
            {t(`team.${member.key}.more`) && (
              <>
                <button
                  onClick={() => toggle(member.name)}
                  className="font-body text-sand/50 text-xs mt-2 hover:text-sand transition-colors flex items-center gap-1 cursor-pointer"
                >
                  {expanded[member.name] ? t('team.lessLabel') : t('team.moreLabel')}
                  <span
                    className={`inline-block transition-transform ${expanded[member.name] ? 'rotate-180' : ''}`}
                  >
                    ▾
                  </span>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${expanded[member.name] ? 'max-h-96 opacity-100 mt-2' : 'max-h-0 opacity-0'}`}
                >
                  <p className="font-body text-sand/70 text-sm leading-relaxed text-center max-w-xs">
                    {t(`team.${member.key}.more`)}
                  </p>
                </div>
              </>
            )}
            <p className="font-body text-rot text-xs mt-3">{member.hashtag}</p>
          </motion.div>
        ))}
      </div>

      {/* Below cards */}
      <div className="text-center pb-16 px-6">
        <p
          className="font-body text-sand/80 text-sm [&_strong]:font-bold [&_strong]:text-sand"
          dangerouslySetInnerHTML={{ __html: t('team.belowCards') }}
        />
      </div>
    </section>
  )
}
